import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { checkCompanyModuleRight } from '../helper';

/** Where a product link lands when the user may open none of the product's own pages. */
export const PRODUCT_LANDING_FALLBACK = '/development-order-overview';

/** Which of a product's pages the user may open, in the order the product sidebar lists them. */
export interface ProductPageAccess {
  productIdentifier: boolean;
  design: boolean;
  bom: boolean;
  measurement: boolean;
  testing: boolean;
  care: boolean;
  samples: boolean;
  costing: boolean;
  purchaseOrder: boolean;
  dam: boolean;
}

/** The slice of the store the landing decision reads. */
export interface ProductLandingState {
  Users?: { logInRights?: unknown; moduleAccess?: { productsAccess?: boolean } };
  UserDetails?: { isCompanyAdminLoggedIn?: boolean; isSupplierLoggedIn?: boolean };
  CompanyRightsReducer?: { companyRightsData?: Record<string, boolean> };
}

interface RightsRow {
  module?: string;
  switchstatus?: boolean;
}

interface RightsGroup {
  rights?: Record<string, RightsRow[] | undefined>;
}

/** Product sub-routes in sidebar order, each paired with the access flag that shows it. */
const PRODUCT_PAGE_ORDER: ReadonlyArray<[keyof ProductPageAccess, string]> = [
  ['productIdentifier', 'product-identifier'],
  ['design', 'design-details/list'],
  ['bom', 'bom'],
  ['measurement', 'measurement'],
  ['testing', 'testing/testing-requirements'],
  ['care', 'care-user-instruction'],
  ['samples', 'samples'],
  ['costing', 'product-costing'],
  ['purchaseOrder', 'product-purchase-order'],
  ['dam', 'digital-assets-management'],
];

/**
 * The first product page the user may open, following the product sidebar's order, or the product
 * list when none is open to them.
 * @param devOrderId - Product (development order) id.
 * @param access - Which product pages the user may open.
 * @returns The route to navigate to.
 */
export function resolveProductLandingPath(devOrderId: string, access: ProductPageAccess): string {
  if (!devOrderId) return PRODUCT_LANDING_FALLBACK;
  const firstAllowed = PRODUCT_PAGE_ORDER.find(([key]) => access[key]);
  return firstAllowed ? `/development-order/${devOrderId}/${firstAllowed[1]}` : PRODUCT_LANDING_FALLBACK;
}

/**
 * Whether any of the user's groups has a module switched on — the rule `CheckModulePermission` in
 * `useComponent` applies. Restated as a pure function so a link can read it through a narrow
 * selector: `useComponent` subscribes to the whole store, which would re-render every row and grid
 * cell that renders a product link on any store change.
 * @param rights - The user's group rights (`Users.logInRights`).
 * @param section - Rights section, e.g. `products`.
 * @param module - Module within the section.
 * @param isAdmin - Company admins pass regardless of group rights.
 * @returns True when the module is switched on for the user.
 */
export function hasModuleSwitchedOn(rights: unknown, section: string, module: string, isAdmin: boolean): boolean {
  if (isAdmin) return true;
  if (!Array.isArray(rights)) return false;
  return (rights as RightsGroup[]).some((group) => {
    const rows = group?.rights?.[section];
    if (!Array.isArray(rows)) return false;
    return rows.find((row) => row?.module === module)?.switchstatus === true;
  });
}

/**
 * Reads, from the store, which product pages the user may open — the same checks the product
 * sidebar (`DevorderUrl.js`) applies to each of its icons.
 * @param state - Redux store state.
 * @returns The access flags.
 */
export function productPageAccessOf(state: ProductLandingState): ProductPageAccess {
  const rights = state.Users?.logInRights;
  const isAdmin = Boolean(state.UserDetails?.isCompanyAdminLoggedIn);
  const companyRights = state.CompanyRightsReducer?.companyRightsData ?? {};
  const allowed = (module: string): boolean => hasModuleSwitchedOn(rights, 'products', module, isAdmin);
  const companyHas = (module: string): boolean => checkCompanyModuleRight(companyRights, module);
  return {
    productIdentifier: Boolean(state.Users?.moduleAccess?.productsAccess) && allowed('productIdentifier'),
    design: allowed('design'),
    bom: allowed('bom'),
    measurement: allowed('measurement'),
    testing: companyHas('testing') && allowed('testing'),
    care: allowed('care'),
    samples: allowed('samples'),
    costing: companyHas('productCosting') && allowed('costing'),
    purchaseOrder: companyHas('purchaseOrder') && allowed('purchaseOrder'),
    dam: companyHas('dam') && !state.UserDetails?.isSupplierLoggedIn && allowed('DAM'),
  };
}

/**
 * Where a link to a product should land for the logged-in user. Product links used to point at the
 * Product Identifier page unconditionally, so a user without that right was bounced off the product
 * entirely. The selector returns a primitive key, so a component using this re-renders only when
 * the user's product access actually changes.
 * @returns A function mapping a product id to its landing route.
 */
export const useProductLandingPath = (): ((devOrderId: string) => string) => {
  const accessKey = useSelector((state: ProductLandingState) => {
    const access = productPageAccessOf(state);
    return PRODUCT_PAGE_ORDER.map(([key]) => (access[key] ? '1' : '0')).join('');
  });

  return useCallback(
    (devOrderId: string) => {
      const access = Object.fromEntries(PRODUCT_PAGE_ORDER.map(([key], index) => [key, accessKey[index] === '1'])) as unknown as ProductPageAccess;
      return resolveProductLandingPath(devOrderId, access);
    },
    [accessKey],
  );
};
